import { Platform, ScrollView } from 'react-native';
import React, { useEffect, useRef } from 'react';
import { Theme, ThemedText, ThemedView } from '@app/theme';
import { Controller, useForm } from 'react-hook-form';
import RadioV2 from '@sharedComponents/RadioV2/RadioV2';
import { useTheme } from '@shopify/restyle';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { LENDER_ID } from '@types/regix';
import {
  KYCAdditionalDetailsFormDataTATA,
  MaritalStatus,
} from '../additional-details-type';
import { updateLinkedApplication } from '@store/slices/authSlice';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { PrivateStackParamList } from '@routes/types';
import { kycAdditonalDetailsApi } from '../kycAdditonalDetailsService';
import { showErrorSheet } from '@store/slices/errorSlice';
import Dropdown from '@sharedComponents/Dropdown/Dropdown';
import {
  DSP_EDUCATION_DATA,
  OCCUPATION_DATA,
  OTHER_EDUCATION_DATA, OTHER_INCOME_RANGE_DATA,
  PURPOSE_OF_LOAN_DATA_DSP,
  PURPOSE_OF_LOAN_DATA_TATA, SOURCE_OF_INCOME_DATA,
} from '../additionalDetailsData';
import InputText from '@sharedComponents/TextInput/TextInput';
import Button from '@sharedComponents/Button/Button';
import ShieldCheckIcon from '@app/assets/svg/ShieldCheckIcon';
import { getNextRoute } from '@app/navigationApp/navigationUtil';
import handleApiErr from '@app/helper/handleApiErr';
import { trackEvent } from '@app/utils';
import { ThemedTouchableOpacity } from '@app/theme/Components';


const TataAdditonalDetailsForm = ({ navigation, additionalDetails }: {
  navigation: NativeStackNavigationProp<PrivateStackParamList, 'KYC_ADDITIONAL_DETAILS', undefined>,
  additionalDetails: any
}) => {

  const scrollRef = useRef<ScrollView>(null);
  const userDetails = useAppSelector(state => state.auth.userDetails);
  const dispatch = useAppDispatch();


  const { colors } = useTheme<Theme>();
  const isLenderDSP = userDetails?.linkedApplications[0]?.lenderAccountId?.toLowerCase() === LENDER_ID.DSP;


  const stepResponseObject = additionalDetails?.stepResponseObject;

  const defaultValues = {
    maritalStatus: MaritalStatus.SINGLE,
    qualification: undefined,
    fatherFirstName: '',
    fatherLastName: '',
    purposeOfLoan: undefined,
    incomeRange: undefined,
    employmentStatus: undefined,
    sourceOfIncome: undefined,
    occupation: undefined,
  }


  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting },
    trigger,
    setValue,
  } = useForm<KYCAdditionalDetailsFormDataTATA>({
    defaultValues: defaultValues,
    values: stepResponseObject,
    mode: 'onChange',
  });


  const allRecievedValuesNonNull = stepResponseObject ? 
  Object.keys(defaultValues).every(key => 
      stepResponseObject[key] !== undefined && 
      stepResponseObject[key] !== null && 
      stepResponseObject[key] !== ''
  ) : false;


  useEffect(() => {
    if (stepResponseObject) {
      setValue('maritalStatus', stepResponseObject?.maritalStatus ?? MaritalStatus.SINGLE);
      setValue('qualification', stepResponseObject?.qualification || undefined);
      setValue('fatherFirstName', stepResponseObject?.fatherFirstName || '');
      setValue('fatherLastName', stepResponseObject?.fatherLastName || '');
      setValue('purposeOfLoan', stepResponseObject?.purposeOfLoan || undefined);
      setValue('incomeRange', stepResponseObject?.incomeRange || undefined);
      setValue('employmentStatus', stepResponseObject?.employmentStatus || undefined);
    }
  }, [stepResponseObject]);

  useEffect(() => {
    if (allRecievedValuesNonNull) {
      trigger() // triggers validation manually
    }
  }, [allRecievedValuesNonNull])


  const onSubmit = async (data: KYCAdditionalDetailsFormDataTATA) => {

    if (isValid) {
      try {
        const { motherFirstName, motherLastName, profession, occupation, ...tataData } = data as any;

        const response = await kycAdditonalDetailsApi({
          applicationId: userDetails?.linkedApplications[0]?.applicationId,
          data: tataData
        });
        const updatedApplication = response?.data?.updatedApplicationObj;

        if (updatedApplication) {
          dispatch(updateLinkedApplication(updatedApplication));
        }
        if (Platform.OS === 'web') {
          navigation.navigate(getNextRoute(updatedApplication?.currentStepId, updatedApplication?.applicationState, updatedApplication?.stepStatusMap))
        } else {
          navigation.reset({
            index: 0,
            routes: [ { name: 'HOME' } ,{ name: 'KYC_STEPPER' }, { name: getNextRoute(updatedApplication?.currentStepId, updatedApplication?.applicationState, updatedApplication?.stepStatusMap) }]
          })
        }

      } catch (error: any) {
        if(handleApiErr(error)) {
          dispatch(showErrorSheet({
            isErrorSheetVisible: true,
            errorMessage: error?.data?.message,
            closeOnBackdropPress: true,
            errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
          }));
        }
      }
    }
  };

  return (
    <>
      <ScrollView ref={scrollRef}>
        {isSubmitting && <ThemedView zIndex={100} opacity={0.5} position='absolute' top={0} left={0} right={0} bottom={0} flex={1} bg='bg-surface-primary' justifyContent='center' alignItems='center'/>}
        <ThemedView padding="lg" paddingTop="2xl" flex={1}>
          <ThemedText variant="h5s">Additional information</ThemedText>
          <ThemedText mt="xl" variant="b3s">Marital status</ThemedText>
          {/* Marital status */}


          <Controller
            control={control}
            name="maritalStatus"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <ThemedView mt="md" flexDirection="row" alignItems="center">
                <ThemedTouchableOpacity onPress={() => onChange(MaritalStatus.SINGLE)} mr="2xl" flexDirection="row"
                                        alignItems="center">
                  <RadioV2 isSelected={value === MaritalStatus.SINGLE} />
                  <ThemedText ml="sm" variant="b3m">Single</ThemedText>
                </ThemedTouchableOpacity>
                <ThemedTouchableOpacity onPress={() => onChange(MaritalStatus.MARRIED)} ml="2xl" flexDirection="row"
                                        alignItems="center">
                  <RadioV2 isSelected={value === MaritalStatus.MARRIED} />
                  <ThemedText ml="sm" variant="b3m">Married</ThemedText>
                </ThemedTouchableOpacity>
              </ThemedView>
            )}
          />
          {/* Qualification */}

          <ThemedText marginVertical="md" variant="b3s">Qualification</ThemedText>
          <Controller
            control={control}
            name="qualification"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Dropdown
                scrollRef={scrollRef}
                options={isLenderDSP ? DSP_EDUCATION_DATA : OTHER_EDUCATION_DATA}
                selectedOption={(isLenderDSP ? DSP_EDUCATION_DATA.find(e => e?.value === value) : OTHER_EDUCATION_DATA.find(e => e?.value === value))}
                onSelect={(e) => {
                  onChange(e.value);
                }}
                label="Qualification"
              />
            )}
          />
          <ThemedText marginTop="md" variant="b3s">Father's name</ThemedText>
          <ThemedView flexDirection="row" alignItems="center">
            <ThemedView flex={1}>
              <Controller
                control={control}
                rules={{ required: true }}
                name="fatherFirstName"
                render={({ field: { onChange, value } }) => (
                  <InputText
                    isDarkMode={Boolean(false)}
                    keyboardType="default"
                    label="First name"
                    showLabel={true}
                    showAnimation
                    value={value}
                    onChangeText={(e) => {
                      const cleaned = e.replace(/^[\s.]+/, '').replace(/[^a-zA-Z\s.]/g, '')
                      onChange(cleaned)
                    }}
                    showErrorMessage={false}
                    showRightAccessory={true}
                    autoFocus={false}
                    rightAccessory={() => null}
                  />
                )}
              />
            </ThemedView>
            <ThemedView ml="md" flex={1}>
              <Controller
                control={control}
                rules={{ required: true }}
                name="fatherLastName"
                render={({ field: { onChange, value } }) => (
                  <InputText
                    isDarkMode={Boolean(false)}
                    keyboardType="default"
                    label="Last name"
                    showLabel={true}
                    showAnimation
                    value={value}
                    onChangeText={(e) => {
                      const cleaned = e.replace(/^[\s.]+/, '').replace(/[^a-zA-Z\s.]/g, '')
                      onChange(cleaned)
                    }}
                    showErrorMessage={false}
                    showRightAccessory={true}
                    autoFocus={false}
                    rightAccessory={() => null}
                  />
                )}
              />
            </ThemedView>
          </ThemedView>


          {/* Purpose of loan */}
          <ThemedText marginVertical="md" variant="b3s">Purpose of loan</ThemedText>
          <Controller
            control={control}
            name="purposeOfLoan"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Dropdown
                scrollRef={scrollRef}
                options={PURPOSE_OF_LOAN_DATA_TATA}
                selectedOption={isLenderDSP ? PURPOSE_OF_LOAN_DATA_TATA.find(e => e?.value === value) : PURPOSE_OF_LOAN_DATA_TATA.find(e => e?.value === value)}
                onSelect={(e) => {
                  onChange(e.value);
                }}
                label="Purpose of loan"
              />
            )}
          />


          <ThemedText marginVertical="md" variant="b3s">Occupation</ThemedText>
          <Controller
            control={control}
            name="employmentStatus"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Dropdown
                scrollRef={scrollRef}
                options={OCCUPATION_DATA}
                selectedOption={OCCUPATION_DATA.find(e => e?.value === value)}
                onSelect={(e) => {
                  onChange(e.value);
                }}
                label="Occupation"
              />
            )}
          />


          <ThemedText marginVertical="md" variant="b3s">Source of income</ThemedText>
          <Controller
            control={control}
            name="sourceOfIncome"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Dropdown
                scrollRef={scrollRef}
                options={SOURCE_OF_INCOME_DATA}
                selectedOption={SOURCE_OF_INCOME_DATA.find(e => e?.value === value)}
                onSelect={(e) => {
                  onChange(e.value);
                }}
                label="Source of income"
              />
            )}
          />

          <ThemedText marginVertical="md" variant="b3s">Income range</ThemedText>
          <Controller
            control={control}
            name="incomeRange"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Dropdown
                scrollRef={scrollRef}
                options={OTHER_INCOME_RANGE_DATA}
                selectedOption={OTHER_INCOME_RANGE_DATA.find(e => e?.value === value)}
                onSelect={(e) => {
                  onChange(e.value);
                }}
                label="Income range"
              />
            )}
          />
          <ThemedView height={100} />
        </ThemedView>
      </ScrollView>
      <ThemedView
        style={{ width: '100%' }}
        bg="bg-surface-primary"
        padding="lg"
        borderTopWidth={1}
        borderTopColor="border-primary"
      >
        <ThemedView mb="md" flexDirection="row" justifyContent="center" alignItems="center">
          <ShieldCheckIcon size={14} />
          <ThemedText textAlign="center" ml="sm" color="text-emphasis-secondary" variant="b4r">
            Your data is secure with us
          </ThemedText>
        </ThemedView>
        <Button
          label="Continue"
          type="SOLID"
          handlePress={() => {
            trackEvent('KYC_ADDITIONAL_DETAILS_CONTINUE_CLICKED')
            handleSubmit(onSubmit)();
          }}
          disabled={!isValid}
          size="MD"
          buttonLoader={isSubmitting}
          bg={colors['bg-action-primary']}
          textColor={'white'}
        />
      </ThemedView>
    </>
  );
};

export default TataAdditonalDetailsForm;
